/** @jsx jsx */
import React from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import Heading from '@atlaskit/heading';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';

import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { reportCategory_reportsOverviewPage$key } from '@atlassian/jira-relay/src/__generated__/reportCategory_reportsOverviewPage.graphql';
import { ReportCard } from '../report-card/index.tsx';

export const ReportCategory = ({
	category,
	shouldShowHeading,
}: {
	category: reportCategory_reportsOverviewPage$key;
	shouldShowHeading: boolean;
}) => {
	const reportCategory = useFragment<reportCategory_reportsOverviewPage$key>(
		graphql`
			fragment reportCategory_reportsOverviewPage on JiraReportCategoryNode {
				name @required(action: THROW)
				reports @required(action: THROW) {
					edges @required(action: THROW) {
						node @required(action: THROW) {
							__typename
							... on JiraReport {
								name @required(action: THROW)
								...reportCard_reportsOverviewPage
							}
						}
					}
				}
			}
		`,
		category,
	);

	const reports = reportCategory.reports.edges.flatMap((reportEdge) =>
		reportEdge && reportEdge.node && reportEdge.node.__typename === 'JiraReport'
			? [reportEdge.node]
			: [],
	);

	const shouldShowHeadingForCategory = reports.length > 0 && shouldShowHeading;

	return (
		<Box as="section">
			<JSErrorBoundary
				id="reportsOverviewPageReportHeading"
				packageName="jiraReportsOverviewPage"
				teamName="helios-headcoach"
				fallback="unmount"
			>
				{shouldShowHeadingForCategory && (
					<div css={headingStyles}>
						<Heading as="h2" size="medium">
							{reportCategory.name}
						</Heading>
					</div>
				)}
			</JSErrorBoundary>
			<Box xcss={gridContainerStyles} paddingBlockStart="space.200" paddingBlockEnd="space.400">
				{reports.map((report) => (
					<JSErrorBoundary
						key={report.name}
						id="reportsOverviewPageReportCard"
						packageName="jiraReportsOverviewPage"
						teamName="helios-headcoach"
						fallback="unmount"
					>
						<ReportCard report={report} />
					</JSErrorBoundary>
				))}
			</Box>
		</Box>
	);
};

const headingStyles = css({
	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
	// @ts-expect-error - need to apply custom tokens for sufficient text contrast in themed projects
	color: Tokens.COLOR_TEXT,
	// @ts-expect-error - need to apply custom tokens for sufficient text contrast in themed projects
	background: Tokens.ELEVATION_SURFACE_CONTRAST,
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
	font: token('font.heading.medium'),
	paddingTop: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	marginTop: token('space.negative.025'),
	marginBottom: token('space.negative.025'),
	marginLeft: token('space.negative.100'),
	marginRight: token('space.negative.100'),
	borderRadius: token('border.radius.100'),
	width: 'fit-content',
});

const gridContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	gap: 'space.300',
	alignItems: 'start',
	maxWidth: '3000px',
});
